import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  DownloadSimple,
  FloppyDisk,
  CircleNotch,
  ArrowsClockwise,
  Check,
  X,
  GameController,
  Scissors,
  Printer,
  UploadSimple,
  Lightning,
  ArrowRight,
} from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import Dropzone from "../components/Dropzone";
import CompatibilityPanel from "../components/CompatibilityPanel";

const PRESET_ICONS = {
  gt7_livery: GameController,
  vinyl_cricut: Scissors,
  print_general: Printer,
};

const POLL_MS = 1000;

export default function Convert() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [presets, setPresets] = useState([]);
  const [preset, setPreset] = useState("gt7_livery");
  const [blackWhite, setBlackWhite] = useState(false);

  // Isolated, opt-in background remover (post-step — does NOT touch conversion).
  const [removeBg, setRemoveBg] = useState(false);
  const [bgRemovedSvg, setBgRemovedSvg] = useState(null);
  const [bgInfo, setBgInfo] = useState(null);
  const [bgLoading, setBgLoading] = useState(false);

  const [job, setJob] = useState(null); // { job_id, status, queue_position, eta_seconds, result, error }
  const [result, setResult] = useState(null);
  const [blocker, setBlocker] = useState(null);
  const [usage, setUsage] = useState(null);

  const pollRef = useRef(null);
  const missRef = useRef(0);

  const activePreset = useMemo(
    () => presets.find((p) => p.key === preset),
    [presets, preset]
  );
  const bwSupported = activePreset ? activePreset.black_white_supported : true;
  const busy = job && (job.status === "queued" || job.status === "processing");

  // What we preview/download: the bg-removed SVG only when the toggle is ON
  // and a result exists. Toggling OFF instantly reverts to the untouched SVG.
  const displaySvg = removeBg && bgRemovedSvg ? bgRemovedSvg : result?.svg;
  const displaySize = removeBg && bgInfo?.changed ? bgInfo.size_bytes : result?.size_bytes;

  // ---- load presets + usage ----
  useEffect(() => {
    api
      .get("/convert/presets")
      .then((r) => setPresets(r.data.presets || []))
      .catch(() => setPresets([]));
  }, []);

  const reloadUsage = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/usage/me");
      setUsage(r.data);
    } catch {
      /* noop */
    }
  }, [user]);

  useEffect(() => {
    reloadUsage();
  }, [reloadUsage]);

  // ---- preview ----
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onFile = (f) => {
    setFile(f);
    setResult(null);
    setJob(null);
    setBlocker(null);
  };

  // ---- polling ----
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollJob = useCallback(
    async (jobId) => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        missRef.current = 0;
        setJob(data);
        if (data.status === "done") {
          stopPolling();
          setResult(data.result);
          toast.success(`Converted in ${(data.result?.total_ms / 1000).toFixed(1)}s`);
        } else if (data.status === "error") {
          stopPolling();
          toast.error(data.error || "Conversion failed");
        } else if (data.status === "cancelled") {
          stopPolling();
        }
      } catch (e) {
        // A 404 can happen transiently if the backend restarted or the
        // request hit another instance — retry a few times before giving up.
        if (e.response?.status === 404) {
          missRef.current += 1;
          if (missRef.current < 5) return;
          stopPolling();
          toast.error("We lost track of that conversion. Please run it again.");
          return;
        }
        stopPolling();
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    },
    [stopPolling]
  );

  useEffect(() => () => stopPolling(), [stopPolling]);

  // ---- isolated background-removal post-step ----
  // Reset cached bg-removed output whenever a fresh conversion arrives.
  useEffect(() => {
    setBgRemovedSvg(null);
    setBgInfo(null);
  }, [result]);

  const runBgRemoval = useCallback(async () => {
    if (!result?.svg) return;
    setBgLoading(true);
    try {
      const { data } = await api.post("/svg/remove-background", { svg: result.svg });
      setBgRemovedSvg(data.svg);
      setBgInfo(data);
      if (!data.changed) toast.info("No background layer found to remove for this image.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setRemoveBg(false);
    } finally {
      setBgLoading(false);
    }
  }, [result]);

  // Compute on demand: only when the toggle is ON, a result exists, and we
  // haven't computed it yet. Zero overhead while the toggle is OFF.
  useEffect(() => {
    if (removeBg && result?.svg && bgRemovedSvg === null && !bgLoading) {
      runBgRemoval();
    }
  }, [removeBg, result, bgRemovedSvg, bgLoading, runBgRemoval]);


  const convert = async () => {
    if (!file) {
      toast.error("Upload an image first");
      return;
    }
    setResult(null);
    setBlocker(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("preset", preset);
    fd.append("black_white", bwSupported && blackWhite ? "true" : "false");
    try {
      const { data } = await api.post("/convert", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJob(data);
      if (data.status === "done") {
        setResult(data.result);
        toast.success("Loaded from cache instantly");
        return;
      }
      stopPolling();
      missRef.current = 0;
      pollRef.current = setInterval(() => pollJob(data.job_id), POLL_MS);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && detail.code === "AUTH_REQUIRED") {
        setBlocker({ kind: "auth", title: "Sign in required", message: detail.message });
        return;
      }
      if (detail && (detail.code === "BUSY")) {
        toast.info(detail.message);
        return;
      }
      if (e.response?.status === 402 || detail?.code === "NO_CREDITS" || detail?.code === "NO_PLAN") {
        setBlocker({
          kind: "credits",
          title: "Out of conversions",
          message: formatApiErrorDetail(detail),
        });
        return;
      }
      toast.error(formatApiErrorDetail(detail) || e.message);
    }
  };

  const cancel = async () => {
    if (!job?.job_id) return;
    try {
      await api.post(`/jobs/${job.job_id}/cancel`);
    } catch {
      /* noop */
    }
    stopPolling();
    setJob(null);
  };

  const chargeIfNeeded = async () => {
    if (!result?.conversion_id) return;
    try {
      await api.post(`/convert/${result.conversion_id}/charge`);
      await reloadUsage();
    } catch (e) {
      console.warn("charge failed", e);
    }
  };

  const download = async () => {
    if (!displaySvg) return;
    await chargeIfNeeded();
    const blob = new Blob([displaySvg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `decal-${preset}-${Date.now()}.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Downloaded");
  };

  const saveToHistory = async () => {
    if (!displaySvg) return;
    if (!user) {
      toast.error("Sign in to save");
      return;
    }
    try {
      await chargeIfNeeded();
      await api.post("/conversions", {
        name: file?.name || `decal-${preset}`,
        svg: displaySvg,
        size_bytes: displaySize,
        compatible: result.compatible,
        mode: preset,
      });
      toast.success("Saved to your history");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const usageLabel = useMemo(() => {
    if (!usage) return null;
    if (usage.unlimited) return "ADMIN · UNLIMITED";
    if (usage.total_credits != null) {
      const total = usage.total_credits;
      return `${total} CONVERSION${total === 1 ? "" : "S"} LEFT`;
    }
    return null;
  }, [usage]);

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--fg)]" data-testid="convert-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Converter</h1>
            <p className="text-sm text-[var(--muted-foreground)] mt-2 max-w-lg">
              Pick a preset, upload your image, and convert. We handle the rest — clean, optimised, reliable output every time.
            </p>
          </div>
          {usageLabel && (
            <span
              className="text-xs font-mono px-3 py-1.5 border border-[var(--border)] bg-white"
              data-testid="usage-chip"
            >
              {usageLabel}
            </span>
          )}
        </div>

        {usage && !usage.unlimited && (usage.total_credits ?? 99) <= 1 && (
          <Link
            to="/pricing"
            data-testid="low-balance-nudge"
            className="group mb-8 flex items-center justify-between gap-4 border-2 border-[var(--accent)] bg-[var(--accent)]/5 px-4 py-3 hover:bg-[var(--accent)]/10 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center bg-[var(--accent)] text-white shrink-0">
                <Lightning size={16} weight="fill" />
              </span>
              <div className="text-sm">
                <span className="font-bold">
                  {(usage.total_credits ?? 0) <= 0
                    ? "You're out of conversions."
                    : "Last conversion left."}
                </span>{" "}
                <span className="text-[var(--fg-2)]">
                  Grab a pack from <span className="font-mono font-bold text-[var(--fg)]">R15</span> — credits never expire.
                </span>
              </div>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-[var(--accent)] whitespace-nowrap group-hover:gap-2.5 transition-all">
              Buy a pack <ArrowRight size={14} weight="bold" />
            </span>
          </Link>
        )}


        <div className="grid lg:grid-cols-2 gap-8">
          {/* ---------- Left: controls ---------- */}
          <div className="space-y-6">
            {/* Upload */}
            <section className="border border-[var(--border)] bg-white" data-testid="upload-section">
              <div className="px-5 py-3 border-b border-[var(--border)] text-xs font-mono uppercase tracking-wider">
                1 · Upload
              </div>
              <div className="p-5">
                {previewUrl ? (
                  <div className="space-y-3">
                    <div
                      className="border border-[var(--border)] aspect-video flex items-center justify-center overflow-hidden"
                      style={{
                        backgroundImage:
                          "linear-gradient(45deg,#eee 25%,transparent 25%),linear-gradient(-45deg,#eee 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#eee 75%),linear-gradient(-45deg,transparent 75%,#eee 75%)",
                        backgroundSize: "16px 16px",
                        backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
                      }}
                    >
                      <img
                        src={previewUrl}
                        alt="original"
                        className="max-h-full max-w-full object-contain"
                        data-testid="original-preview"
                      />
                    </div>
                    <button
                      onClick={() => onFile(null)}
                      className="text-xs font-mono flex items-center gap-1 hover:text-[var(--accent)]"
                      data-testid="clear-file-btn"
                      disabled={busy}
                    >
                      <UploadSimple size={14} /> Choose a different image
                    </button>
                  </div>
                ) : (
                  <Dropzone onFile={onFile} />
                )}
              </div>
            </section>

            {/* Presets */}
            <section className="border border-[var(--border)] bg-white" data-testid="preset-section">
              <div className="px-5 py-3 border-b border-[var(--border)] text-xs font-mono uppercase tracking-wider">
                2 · Choose a preset
              </div>
              <div className="p-5 space-y-3">
                {presets.map((p) => {
                  const Icon = PRESET_ICONS[p.key] || GameController;
                  const active = preset === p.key;
                  return (
                    <button
                      key={p.key}
                      onClick={() => setPreset(p.key)}
                      disabled={busy}
                      data-testid={`preset-${p.key}`}
                      className={`w-full text-left p-4 border transition-colors ${
                        active
                          ? "border-[var(--accent)] bg-[var(--accent)]/5"
                          : "border-[var(--border)] hover:border-[var(--fg)]"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon size={22} weight={active ? "fill" : "regular"} className={active ? "text-[var(--accent)]" : ""} />
                        <div className="flex-1">
                          <div className="font-bold flex items-center gap-2">
                            {p.label}
                            {active && <Check size={16} className="text-[var(--accent)]" />}
                          </div>
                          <div className="text-xs text-[var(--muted-foreground)] mt-0.5">{p.description}</div>
                          <div className="text-[10px] font-mono mt-1 text-[var(--muted-foreground)]">
                            {p.target_kb ? `TARGET ≤ ${p.target_kb}KB` : `MAX ${p.max_output_kb}KB`}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* B&W + convert */}
            <section className="border border-[var(--border)] bg-white" data-testid="options-section">
              <div className="p-5 space-y-4">
                <label
                  className={`flex items-center justify-between gap-3 ${bwSupported ? "" : "opacity-40"}`}
                  data-testid="toggle-black-white"
                >
                  <div>
                    <div className="font-bold text-sm">Black &amp; White</div>
                    <div className="text-xs text-[var(--muted-foreground)]">
                      {bwSupported
                        ? "Trace as a single black layer"
                        : "This preset is always single-colour"}
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="w-5 h-5 accent-[var(--accent)]"
                    checked={bwSupported ? blackWhite : true}
                    disabled={!bwSupported || busy}
                    onChange={(e) => setBlackWhite(e.target.checked)}
                    data-testid="checkbox-black-white"
                  />
                </label>

                {/* Isolated, opt-in background remover — post-step only. */}
                <label
                  className="flex items-center justify-between gap-3 pt-4 border-t border-[var(--border)]"
                  data-testid="toggle-remove-bg"
                >
                  <div>
                    <div className="font-bold text-sm flex items-center gap-2">
                      Remove Background
                      {bgLoading && <CircleNotch size={14} className="animate-spin text-[var(--accent)]" />}
                    </div>
                    <div className="text-xs text-[var(--muted-foreground)]">
                      Clears the background &amp; filled letter holes from the output. Doesn&apos;t affect the SVG conversion.
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="w-5 h-5 accent-[var(--accent)]"
                    checked={removeBg}
                    onChange={(e) => setRemoveBg(e.target.checked)}
                    data-testid="checkbox-remove-bg"
                  />
                </label>
                {removeBg && bgInfo && (
                  <div className="text-[11px] font-mono text-[var(--muted-foreground)]" data-testid="remove-bg-status">
                    {bgInfo.changed
                      ? `Removed ${bgInfo.removed_paths} background layer(s)${bgInfo.removed_colors?.length ? ` (${bgInfo.removed_colors.join(", ")})` : ""} · now ${(bgInfo.size_bytes / 1024).toFixed(1)}KB`
                      : "No background layer detected for this image."}
                  </div>
                )}

                {busy ? (
                  <button
                    onClick={cancel}
                    className="w-full py-3 border-2 border-[var(--fg)] font-bold flex items-center justify-center gap-2 hover:bg-[var(--fg)] hover:text-white transition-colors"
                    data-testid="cancel-job-btn"
                  >
                    <X size={18} /> Cancel
                  </button>
                ) : (
                  <button
                    onClick={convert}
                    disabled={!file}
                    className="w-full py-3 bg-[var(--accent)] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-40 hover:bg-[var(--accent-hover)] transition-colors"
                    data-testid="convert-btn"
                  >
                    Convert
                  </button>
                )}
              </div>
            </section>

            {/* Blockers */}
            {blocker && (
              <div
                className="border-2 border-[var(--accent)] bg-[var(--accent)]/5 p-5"
                data-testid="blocker-banner"
              >
                <div className="font-bold">{blocker.title}</div>
                <div className="text-sm mt-1">{blocker.message}</div>
                <div className="mt-3 flex gap-3">
                  {blocker.kind === "auth" ? (
                    <Link to="/register" className="text-sm font-bold underline" data-testid="blocker-signin-link">
                      Sign in or create account
                    </Link>
                  ) : (
                    <Link to="/pricing" className="text-sm font-bold underline" data-testid="blocker-pricing-link">
                      See conversion packs
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ---------- Right: status + result ---------- */}
          <div className="space-y-6">
            {/* Status */}
            {busy && (
              <section
                className="border border-[var(--border)] bg-white p-6 text-center"
                data-testid="job-status"
              >
                <CircleNotch size={40} className="mx-auto animate-spin text-[var(--accent)]" />
                {job.status === "queued" ? (
                  <div className="mt-4">
                    <div className="font-bold" data-testid="queue-position">
                      In queue · position {job.queue_position}
                    </div>
                    <div className="text-sm text-[var(--muted-foreground)] mt-1" data-testid="eta">
                      Estimated wait ~{Math.max(1, Math.round(job.eta_seconds || 0))}s
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 font-bold" data-testid="processing-label">
                    Tracing your image…
                  </div>
                )}
                <p className="text-xs text-[var(--muted-foreground)] mt-3">
                  We never lower quality under load — your conversion is queued for a full-quality trace.
                </p>
              </section>
            )}

            {/* Result */}
            {result && !busy && (
              <>
                <section className="border border-[var(--border)] bg-white" data-testid="result-section">
                  <div className="px-5 py-3 border-b border-[var(--border)] text-xs font-mono uppercase tracking-wider flex items-center justify-between">
                    <span>Result</span>
                    <span className={result.compatible ? "text-[var(--success)]" : "text-[var(--warning)]"}>
                      {result.compatible ? "✓ COMPATIBLE" : "⚠ OVERSIZE"}
                    </span>
                  </div>
                  <div
                    className="aspect-video flex items-center justify-center p-4"
                    style={{
                      backgroundImage:
                        "linear-gradient(45deg,#eee 25%,transparent 25%),linear-gradient(-45deg,#eee 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#eee 75%),linear-gradient(-45deg,transparent 75%,#eee 75%)",
                      backgroundSize: "16px 16px",
                      backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
                    }}
                    dangerouslySetInnerHTML={{ __html: displaySvg }}
                    data-testid="svg-preview"
                  />
                  <div className="p-5 grid grid-cols-2 gap-3">
                    <button
                      onClick={download}
                      className="py-3 bg-[var(--fg)] text-white font-bold flex items-center justify-center gap-2 hover:bg-[var(--accent)] transition-colors"
                      data-testid="download-btn"
                    >
                      <DownloadSimple size={18} /> Download SVG
                    </button>
                    <button
                      onClick={saveToHistory}
                      className="py-3 border border-[var(--border)] font-bold flex items-center justify-center gap-2 hover:border-[var(--fg)] transition-colors"
                      data-testid="save-btn"
                    >
                      <FloppyDisk size={18} /> Save
                    </button>
                  </div>
                </section>

                <CompatibilityPanel result={result} />
              </>
            )}

            {!busy && !result && (
              <section className="border border-dashed border-[var(--border)] p-10 text-center text-sm text-[var(--muted-foreground)]" data-testid="result-placeholder">
                <ArrowsClockwise size={32} className="mx-auto mb-3 opacity-40" />
                Your converted SVG will appear here.
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
